import React from 'react';
import PropTypes from 'prop-types';

/**
 * HoF that accepts a context and callback and returns
 * a function that logs debug information to the console before
 * calling the callbackFunction.
 *
 * @param {Object} ctx
 * @param {Function} cbFn
 * @return {function({key: string, value?: *})}
 */
var debug = (function (ctx, cbFn) {
  return function (_ref) {
    var key = _ref.key,
        value = _ref.value;

    console.group('spForm: handleChange');
    console.log('handleChange called for "' + key + '" with value: ', value);
    console.log(ctx, key);
    if (ctx && ctx.elementsWithOnChangeReset_ && ctx.elementsWithOnChangeReset_[key]) {
      console.log('%cThe key ' + ctx.elementsWithOnChangeReset_[key] + ' will be reset.', 'color: red;');
    }
    console.groupEnd();
    cbFn({ key: key, value: value });
  };
});

var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) {
  return typeof obj;
} : function (obj) {
  return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
};

var classCallCheck = function (instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
};

var createClass = function () {
  function defineProperties(target, props) {
    for (var i = 0; i < props.length; i++) {
      var descriptor = props[i];
      descriptor.enumerable = descriptor.enumerable || false;
      descriptor.configurable = true;
      if ("value" in descriptor) descriptor.writable = true;
      Object.defineProperty(target, descriptor.key, descriptor);
    }
  }

  return function (Constructor, protoProps, staticProps) {
    if (protoProps) defineProperties(Constructor.prototype, protoProps);
    if (staticProps) defineProperties(Constructor, staticProps);
    return Constructor;
  };
}();

var defineProperty = function (obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
};

var _extends = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

var inherits = function (subClass, superClass) {
  if (typeof superClass !== "function" && superClass !== null) {
    throw new TypeError("Super expression must either be null or a function, not " + typeof superClass);
  }

  subClass.prototype = Object.create(superClass && superClass.prototype, {
    constructor: {
      value: subClass,
      enumerable: false,
      writable: true,
      configurable: true
    }
  });
  if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass;
};

var objectWithoutProperties = function (obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
};

var possibleConstructorReturn = function (self, call) {
  if (!self) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return call && (typeof call === "object" || typeof call === "function") ? call : self;
};

var toConsumableArray = function (arr) {
  if (Array.isArray(arr)) {
    for (var i = 0, arr2 = Array(arr.length); i < arr.length; i++) arr2[i] = arr[i];

    return arr2;
  } else {
    return Array.from(arr);
  }
};

var isDate = function isDate(d) {
  return d instanceof Date;
};
var isEmpty = function isEmpty(o) {
  return Object.keys(o).length === 0;
};
var isObject = function isObject(o) {
  return o != null && (typeof o === 'undefined' ? 'undefined' : _typeof(o)) === 'object';
};
var properObject = function properObject(o) {
  return isObject(o) && !o.hasOwnProperty ? _extends({}, o) : o;
};

var diff = function diff(lhs, rhs) {
  if (lhs === rhs) return {}; // equal return no diff

  if (!isObject(lhs) || !isObject(rhs)) return rhs; // return updated rhs

  var l = properObject(lhs);
  var r = properObject(rhs);

  var deletedValues = Object.keys(l).reduce(function (acc, key) {
    return r.hasOwnProperty(key) ? acc : _extends({}, acc, defineProperty({}, key, undefined));
  }, {});

  if (isDate(l) || isDate(r)) {
    if (l.valueOf() == r.valueOf()) return {};
    return r;
  }

  return Object.keys(r).reduce(function (acc, key) {
    if (!l.hasOwnProperty(key)) return _extends({}, acc, defineProperty({}, key, r[key])); // return added r key

    var difference = diff(l[key], r[key]);

    if (isObject(difference) && isEmpty(difference) && !isDate(difference)) return acc; // return no diff

    return _extends({}, acc, defineProperty({}, key, difference)); // return updated key
  }, deletedValues);
};

var addedDiff = function addedDiff(lhs, rhs) {

  if (lhs === rhs || !isObject(lhs) || !isObject(rhs)) return {};

  var l = properObject(lhs);
  var r = properObject(rhs);

  return Object.keys(r).reduce(function (acc, key) {
    if (l.hasOwnProperty(key)) {
      var difference = addedDiff(l[key], r[key]);

      if (isObject(difference) && isEmpty(difference)) return acc;

      return _extends({}, acc, defineProperty({}, key, difference));
    }

    return _extends({}, acc, defineProperty({}, key, r[key]));
  }, {});
};

var deletedDiff = function deletedDiff(lhs, rhs) {
  if (lhs === rhs || !isObject(lhs) || !isObject(rhs)) return {};

  var l = properObject(lhs);
  var r = properObject(rhs);

  return Object.keys(l).reduce(function (acc, key) {
    if (r.hasOwnProperty(key)) {
      var difference = deletedDiff(l[key], r[key]);

      if (isObject(difference) && isEmpty(difference)) return acc;

      return _extends({}, acc, defineProperty({}, key, difference));
    }

    return _extends({}, acc, defineProperty({}, key, undefined));
  }, {});
};

var updatedDiff = function updatedDiff(lhs, rhs) {

  if (lhs === rhs) return {};

  if (!isObject(lhs) || !isObject(rhs)) return rhs;

  var l = properObject(lhs);
  var r = properObject(rhs);

  if (isDate(l) || isDate(r)) {
    if (l.valueOf() == r.valueOf()) return {};
    return r;
  }

  return Object.keys(r).reduce(function (acc, key) {

    if (l.hasOwnProperty(key)) {
      var difference = updatedDiff(l[key], r[key]);

      if (isObject(difference) && isEmpty(difference) && !isDate(difference)) return acc;

      return _extends({}, acc, defineProperty({}, key, difference));
    }

    return acc;
  }, {});
};

var detailedDiff = function detailedDiff(lhs, rhs) {
  return {
    added: addedDiff(lhs, rhs),
    deleted: deletedDiff(lhs, rhs),
    updated: updatedDiff(lhs, rhs)
  };
};

var diff$1 = {
  addedDiff: addedDiff,
  diff: diff,
  deletedDiff: deletedDiff,
  updatedDiff: updatedDiff,
  detailedDiff: detailedDiff
};

/**
 * Returns an object with the keys `diff` and `detailedDiff`
 * based on the `deep-object-diff` module. `detailedDiff` contains
 * the keys `added`, `updated`, and `deleted`.
 *
 * @param {Object} initialState
 * @param {Object} formState
 * @return {{diff: Object, detailedDiff: { added: Object, updated: Object, deleted: Object } }}
 */
var getDiff = (function () {
  var initialState = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var formState = arguments[1];
  return {
    diff: /** @type {Object} */diff$1.diff(initialState, formState),
    detailedDiff: /** @type {{  added: Object, updated: Object, deleted: Object }} */diff$1.detailedDiff(initialState, formState)
  };
});

/**
 * Simple hash calculation of provided value. Either
 * turns it into a string or stringify it if object or array.
 * @param value
 * @return {string}
 */
var calculateHashOfValue = function calculateHashOfValue(value) {
  if (Array.isArray(value) || (typeof value === 'undefined' ? 'undefined' : _typeof(value)) === 'object') {
    return JSON.stringify(value);
  }
  return '' + value;
};

/**
 * Given a state object of key/values pairs, creates a string
 * with patterns `key1=value1||key2=value2||`. Meant to be used for small
 * objects with a couple of keys in order to provide a simple hash
 * that’s easy to debug.
 *
 * User is expected to provide their own hashing algorithm for
 * more complex use cases.
 * @param {Object} state
 * @return {string}
 */
var getHash = function getHash(state) {
  return Object.keys(state).reduce(function (acc, key) {
    return '' + acc + key + '=' + calculateHashOfValue(state[key]) + '||';
  }, '');
};

var isMergeableObject = function isMergeableObject(value) {
	return isNonNullObject(value)
		&& !isSpecial(value)
};

function isNonNullObject(value) {
	return !!value && typeof value === 'object'
}

function isSpecial(value) {
	var stringValue = Object.prototype.toString.call(value);

	return stringValue === '[object RegExp]'
		|| stringValue === '[object Date]'
		|| isReactElement(value)
}

// see https://github.com/facebook/react/blob/b5ac963fb791d1298e7f396236383bc955f916c1/src/isomorphic/classic/element/ReactElement.js#L21-L25
var canUseSymbol = typeof Symbol === 'function' && Symbol.for;
var REACT_ELEMENT_TYPE = canUseSymbol ? Symbol.for('react.element') : 0xeac7;

function isReactElement(value) {
	return value.$$typeof === REACT_ELEMENT_TYPE
}

function emptyTarget(val) {
	return Array.isArray(val) ? [] : {}
}

function cloneUnlessOtherwiseSpecified(value, options) {
	return (options.clone !== false && options.isMergeableObject(value))
		? deepmerge(emptyTarget(value), value, options)
		: value
}

function defaultArrayMerge(target, source, options) {
	return target.concat(source).map(function(element) {
		return cloneUnlessOtherwiseSpecified(element, options)
	})
}

function getMergeFunction(key, options) {
	if (!options.customMerge) {
		return deepmerge
	}
	var customMerge = options.customMerge(key);
	return typeof customMerge === 'function' ? customMerge : deepmerge
}

function getEnumerableOwnPropertySymbols(target) {
	return Object.getOwnPropertySymbols
		? Object.getOwnPropertySymbols(target).filter(function(symbol) {
			return target.propertyIsEnumerable(symbol)
		})
		: []
}

function getKeys(target) {
	return Object.keys(target).concat(getEnumerableOwnPropertySymbols(target))
}

function mergeObject(target, source, options) {
	var destination = {};
	if (options.isMergeableObject(target)) {
		getKeys(target).forEach(function(key) {
			destination[key] = cloneUnlessOtherwiseSpecified(target[key], options);
		});
	}
	getKeys(source).forEach(function(key) {
		if (!options.isMergeableObject(source[key]) || !target[key]) {
			destination[key] = cloneUnlessOtherwiseSpecified(source[key], options);
		} else {
			destination[key] = getMergeFunction(key, options)(target[key], source[key], options);
		}
	});
	return destination
}

function deepmerge(target, source, options) {
	options = options || {};
	options.arrayMerge = options.arrayMerge || defaultArrayMerge;
	options.isMergeableObject = options.isMergeableObject || isMergeableObject;

	var sourceIsArray = Array.isArray(source);
	var targetIsArray = Array.isArray(target);
	var sourceAndTargetTypesMatch = sourceIsArray === targetIsArray;

	if (!sourceAndTargetTypesMatch) {
		return cloneUnlessOtherwiseSpecified(source, options)
	} else if (sourceIsArray) {
		return options.arrayMerge(target, source, options)
	} else {
		return mergeObject(target, source, options)
	}
}

deepmerge.all = function deepmergeAll(array, options) {
	if (!Array.isArray(array)) {
		throw new Error('first argument should be an array')
	}

	return array.reduce(function(prev, next) {
		return deepmerge(prev, next, options)
	}, {})
};

var deepmerge_1 = deepmerge;

var cjs = deepmerge_1;

/**
 * Returns a state based on the provider props.
 * Used to initialize this.state when component
 * it mounted.
 *
 * @param props
 * @return {{
 *    initialState: Object,
 *    entityState: Object,
 *    cachedAsyncFields: Object,
 *    validationFeedback: Object,
 *    canSubmitForm: boolean
 *  }}
 */

var getInitialState = (function () {
  var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return {
    initialState: cjs({}, props.initialState || {}),
    entityState: cjs({}, props.initialState || {}),
    cachedAsyncFields: {},
    validationFeedback: {},
    canSubmitForm: true
  };
});

/**
 * Flatters the sections array and produces a list
 * of spec elements.
 * @param {Array} sections
 * @returns {Array}
 */
var flattenSections = function flattenSections(sections) {
  return sections.reduce(function (acc, section) {
    return acc.concat(section.elements);
  }, []).filter(function (_) {
    return !_.isPresentationalElement;
  });
};

/**
 * Creates an object describing visibility dependencies
 * based on the provided array of spec elements.
 *
 * @param {Array} elements
 * @returns {Object}
 */
var formVisibilityDepMap = function formVisibilityDepMap(elements) {
  return elements.filter(function (_) {
    return _.existsIf;
  }).reduce(function (acc, element) {
    element.existsIf.forEach(function (key) {
      if (acc[key] === undefined) {
        acc[key] = [];
      }
      acc[key].push(element.name);
    });
    return acc;
  }, {});
};

/**
 * Creates an object describing onChange dependencies
 * based on the provided array of spec elements.
 *
 * @param {Array} elements
 * @returns {Object}
 */
var formOnChangeDepMap = function formOnChangeDepMap(elements) {
  return elements.filter(function (_) {
    return !!_.onChangeReset;
  }).reduce(function (acc, _) {
    return Object.assign({}, acc, defineProperty({}, _.name, _.onChangeReset));
  }, {});
};

/**
 * Given a list of dependants and the current
 * entity state, returns a new object with the
 * key-value pair of all the dependants.
 *
 * @param {Array.<string>} dependants
 * @param {Object} entityState
 * @returns {Object}
 */
var getStateOfDependants = function getStateOfDependants(dependants, entityState) {
  return dependants.reduce(function (acc, _) {
    return Object.assign({}, acc, defineProperty({}, _, entityState[_]));
  }, {});
};

/**
 * Returns a map of validation rules indexed by
 * every element’s name.
 *
 * @param {Array} elements
 * @returns {Object}
 */
var flattenValidationRules = function flattenValidationRules(elements) {
  return elements.reduce(function (acc, element) {
    return Object.assign({}, acc, defineProperty({}, element.name, element.validationFeedbackRules));
  }, {});
};

/**
 * Returns true if there is validation feedback present
 * for the given parameters by calling the supplied
 * condition function.
 *
 * @param {string} fieldName
 * @param {Object} entityState
 * @param {Function} conditionFn
 * @param {Array.<string>} validateWith
 * @returns {boolean}
 */
var getValidationFeedback = function getValidationFeedback(fieldName, entityState, conditionFn, validateWith) {
  if (Array.isArray(validateWith) && validateWith.length > 0) {
    return conditionFn(Object.assign({}, defineProperty({}, fieldName, entityState[fieldName]), validateWith.reduce(function (acc, field) {
      return Object.assign({}, acc, defineProperty({}, field, entityState[field]));
    }, {})));
  }
  return conditionFn(entityState[fieldName]);
};

/**
 * Returns a feedback object for the provided field.
 * By contract the only important `type` value if `error` which
 * will in turn tell the form submission is not possible.
 * User of this function need only look at the key hasFeedback to
 * determine if any feedback is present for the provided parameters.
 *
 * Not all feedback is negative, users of the framework can also use
 * feedback objects to provide additional information or success messages.
 *
 * @param {string} field
 * @param {Array} validationFeedbackRules
 * @param {Object} entityState
 * @param {boolean} checkOnlyIfCheckOnChangeSpecified
 * @returns {{hasFeedback: boolean, type: string, label: string}}
 */
var getValidateFeedbackForField = function getValidateFeedbackForField(field) {
  var validationFeedbackRules = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  var entityState = arguments[2];
  var checkOnlyIfCheckOnChangeSpecified = arguments[3];

  var feedback = validationFeedbackRules.find(function (rule) {
    return (checkOnlyIfCheckOnChangeSpecified ? rule.checkOnChange : true) && getValidationFeedback(field, entityState, rule.condition, rule.validateWith);
  });

  return {
    hasFeedback: feedback !== undefined,
    type: (feedback || {}).type,
    label: (feedback || {}).label
  };
};

/**
 * Returns true or false on whether the current
 * state can be saved.
 *
 * @param {Object} entityState
 * @param {Function} getFormStateFn
 * @param {Array} elements
 * @param {Object} validationFeedbackRules
 * @returns {boolean}
 */
var canSubmitForm = function canSubmitForm(entityState, getFormStateFn, elements, validationFeedbackRules) {
  var _loop = function _loop(i) {
    var element = elements[i];
    var value = entityState[element.name];
    var existsInFormContext = true;
    if (element.existsIf) {
      existsInFormContext = element.existsIf.reduce(function (acc, cond) {
        return acc && entityState[cond];
      }, true);
    }

    if (existsInFormContext) {
      if (element.validationFeedbackRules) {
        var feedback = validationFeedbackRules[element.name].find(function (rule) {
          return getValidationFeedback(element.name, entityState, rule.condition, rule.validateWith);
        });
        if (feedback && feedback.type === 'error') {
          return {
            v: false
          };
        }
      } else if (element.required) {
        if (value === undefined || value === '') {
          return {
            v: false
          };
        }
      }
    }
    if (i === elements.length - 1) {
      var _getFormStateFn = getFormStateFn(),
          _getFormStateFn$diff = _getFormStateFn.diff,
          diff = _getFormStateFn$diff === undefined ? {} : _getFormStateFn$diff;

      return {
        v: Object.keys(diff.diff || {}).length > 0
      };
    }
  };

  for (var i = 0; i < elements.length; i += 1) {
    var _ret = _loop(i);

    if ((typeof _ret === 'undefined' ? 'undefined' : _typeof(_ret)) === "object") return _ret.v;
  }
  return true;
};

var FormHOC = function FormHOC(_ref) {
  var componentMap = _ref.componentMap,
      wrappers = _ref.wrappers;

  var SpWolfForm = function (_React$Component) {
    inherits(SpWolfForm, _React$Component);

    function SpWolfForm(props) {
      classCallCheck(this, SpWolfForm);

      var _this = possibleConstructorReturn(this, (SpWolfForm.__proto__ || Object.getPrototypeOf(SpWolfForm)).call(this, props));

      _this.processContext = function () {
        var defineStateGetter = _this.context.defineStateGetter;

        defineStateGetter && defineStateGetter(_this.getFormState);
      };

      _this.getFormState = function () {
        return {
          initialState: _this.state.initialState,
          state: _this.state.entityState,
          diff: getDiff(_this.state.initialState, _this.state.entityState)
        };
      };

      _this.updateCanSubmitForm = function (canSubmit) {
        _this.props.onCanSubmitFormChange && _this.props.onCanSubmitFormChange(canSubmit);
      };

      _this.validateForm = function () {
        return _this.updateCanSubmitForm(canSubmitForm(_this.state.entityState, _this.getFormState, _this.elements_, _this.validationFeedbackRules_));
      };

      _this.validateField = function (field, checkOnlyIfCheckOnChangeSpecified) {
        if (_this.validationFeedbackRules_[field]) {
          var validationFeedback = _this.state.validationFeedback;

          validationFeedback[field] = getValidateFeedbackForField(field, _this.validationFeedbackRules_[field], _this.state.entityState, checkOnlyIfCheckOnChangeSpecified);
          _this.setState({ validationFeedback: validationFeedback });
        }
      };

      _this.handleFieldBlur = function (field) {
        _this.validateField(field);
      };

      _this.evaluateAsyncDep = function (fn, dependants) {
        var state = getStateOfDependants(dependants, _this.state.entityState);
        var hash = getHash(state);
        if (_this.state.cachedAsyncFields[hash]) {
          return _this.state.cachedAsyncFields[hash];
        }
        try {
          fn(state).then(function (result) {
            var cache = _this.state.cachedAsyncFields;
            cache[hash] = result;
            _this.setState({ cachedAsyncFields: cache });
          });
        } catch (err) {
          // TODO
        }
        return { asyncPending: true };
      };

      _this.calculateAllConditionalFields = function () {
        var prev = Object.assign({}, _this.state.entityState);
        var next = _this.conditionalFields_.reduce(function (state, field) {
          return Object.assign({}, state, defineProperty({}, field.name, field.fn(_this.state.entityState)));
        }, prev);
        _this.setState({ entityState: next }, function () {
          _this.validateForm();
        });
      };

      _this.handleChange = function (_ref2) {
        var key = _ref2.key,
            value = _ref2.value;

        // Used to reset a field if this behaviour is defined by the spec.
        var withReset = _this.elementsWithOnChangeReset_[key] ? defineProperty({}, _this.elementsWithOnChangeReset_[key], undefined) : {};
        var entityState = Object.assign({}, _this.state.entityState, withReset, defineProperty({}, key, value));
        /*
         * Remove <key> if it was not in the initial state
         * and now equals undefined. This will register as
         * part of the diff { added: { [key]: undefined } },
         * but it is not really something we want in our diff.
         */
        if (!_this.state.initialState.hasOwnProperty(key) && value === undefined) {
          delete entityState[key];
        }
        _this.setState({ entityState: entityState }, function () {
          _this.recalculateRelevantConditionalFields(key);
          _this.validateField(key, true);
        });
      };

      _this.recalculateRelevantConditionalFields = function (key) {
        clearTimeout(_this.timer_);
        var prev = Object.assign({}, _this.state.entityState);
        var next = _this.conditionalFields_.reduce(function (state, field) {
          if (field.dependsOn.some(function (dependant) {
            return dependant === key;
          })) {
            var value = field.fn(_this.state.entityState);
            var inferredUpdates = {};
            if (value === false) {
              inferredUpdates = (_this.visibilityMap_[field.name] || []).reduce(function (acc, _) {
                return Object.assign({}, acc, defineProperty({}, _, undefined));
              }, {});
            }
            return Object.assign({}, state, inferredUpdates, defineProperty({}, field.name, value));
          }
          return state;
        }, prev);
        _this.setState({ entityState: next }, function () {
          _this.timer_ = setTimeout(function () {
            return _this.validateForm();
          }, 250);
        });
      };

      _this.state = getInitialState(props);
      _this.handleChange_ = props.debugOnChange ? debug(_this, _this.handleChange) : _this.handleChange;
      return _this;
    }

    /**
     * Initialize context
     */


    createClass(SpWolfForm, [{
      key: 'componentDidMount',
      value: function componentDidMount() {
        this.processContext();
        var specs = this.props.specs;
        var _specs$conditionalFie = specs.conditionalFields,
            conditionalFields = _specs$conditionalFie === undefined ? [] : _specs$conditionalFie;

        this.conditionalFields_ = conditionalFields;
        this.elements_ = flattenSections(specs.sections);
        this.validationFeedbackRules_ = flattenValidationRules(this.elements_);
        this.visibilityMap_ = formVisibilityDepMap(this.elements_);
        this.elementsWithOnChangeReset_ = formOnChangeDepMap(this.elements_);
        this.calculateAllConditionalFields();
      }

      /**
       *  exposes getFormState to the wrapping connect()
       *  component via context
       */


      /**
       *
       * @returns {{
       *   initialState: Object,
       *   state: Object,
       *   diff: {diff: Object, detailedDiff: {added: Object, updated: Object, deleted: Object}}
       * }}
       */


      /**
       * Passes the canSubmit boolean to the
       * onCanSubmitFormChange prop.
       * @param {boolean} canSubmit
       */


      /**
       * @param {string} field
       */


      /**
       * Creates a hash based on the values of the keys in the
       * dependants array. Then looks if the value for that hash key
       * is saved in state.cachedAsyncField. Returns it if true, otherwise
       * calls the provided asyncDep function and once the result it generated,
       * caches it and sets the state. (Triggering a re-render.)
       *
       * In the meantime, while the promise is left to execute, the function
       * returns an object with the key `asyncPending` true which lets the
       * input component know the value is currently loading.
       * @param {function} fn
       * @param {Array.<string>} dependants
       * @returns {*}
       */


      /**
       * Goes through all of the conditional fields and calculates
       * their values. Finally sets the state.
       * TODO: Need perhaps deep copy?
       */


      /**
       * At minimum, sets the state with the provided key/value
       * pair. It can also:
       *  1. Reset the value of another field if such a
       *  dependency is defined by the spec.
       *  2. Remove a key from the state to avoid an
       *  incorrect diff. (See comment above delete entityState[key].)
       *
       *  As a side effect it will also:
       *  1. Trigger the validation for the field.
       *  2. Call recalculateRelevantConditionalFields() to
       *  allow conditional fields to recalculate.
       *
       * @param {string} key
       * @param {*} value
       */


      /**
       * Given the <key> that just changed, recalculate
       * all relevant conditional fields.
       *
       * Side effects:
       * 1. If there are fields that need to be toggled to invisible
       * now that a conditional field has changed to `false`,
       * then this method will reset them to `undefined`.
       * 2. This will trigger validateForm. We use this.timer_ as
       * a debounce mechanism to prevent form validation for 250ms
       * in case another call is made to this function.
       *
       * @param {string} key
       */

    }, {
      key: 'render',
      value: function render() {
        var _this2 = this;

        var _props = this.props,
            specs = _props.specs,
            sectionProps = _props.sectionProps;
        var _state = this.state,
            entityState = _state.entityState,
            validationFeedback = _state.validationFeedback;

        var Form = wrappers.form;
        var Section = wrappers.section;
        return React.createElement(
          Form,
          null,
          specs.sections.map(function (section, i) {
            return React.createElement(
              Section,
              _extends({
                key: 'section-' + i,
                index: i,
                meta: section.meta || {}
              }, sectionProps),
              section.elements.map(function (_ref4, j) {
                var fieldType = _ref4.fieldType,
                    _ref4$existsIf = _ref4.existsIf,
                    existsIf = _ref4$existsIf === undefined ? [] : _ref4$existsIf,
                    disabledIf = _ref4.disabledIf,
                    asyncEval = _ref4.asyncEval,
                    isPresentationalElement = _ref4.isPresentationalElement,
                    otherProps = objectWithoutProperties(_ref4, ['fieldType', 'existsIf', 'disabledIf', 'asyncEval', 'isPresentationalElement']);

                var FormFieldComponent = componentMap[fieldType];
                var isVisible = existsIf.reduce(function (acc, cond) {
                  return acc && entityState[cond];
                }, true);
                var isDisabled = entityState[disabledIf] || false;

                if (!isVisible) {
                  return null;
                }

                if (isPresentationalElement) {
                  var presentationalData = (otherProps.dependsOn || []).reduce(function (acc, field) {
                    return Object.assign({}, acc, defineProperty({}, field, entityState[field]));
                  }, {});
                  return React.createElement(FormFieldComponent, { key: 'field-' + j, data: presentationalData });
                }

                var inferredProps = {};
                if (asyncEval && Array.isArray(asyncEval) && asyncEval.length > 0) {
                  asyncEval.forEach(function (asyncSpec) {
                    inferredProps[asyncSpec.key] = _this2.evaluateAsyncDep.apply(_this2, toConsumableArray(asyncSpec.config));
                  });
                }
                return React.createElement(FormFieldComponent, _extends({
                  className: 'margin-bottom-12',
                  key: 'field-' + j,
                  onChange: _this2.handleChange_,
                  value: entityState[otherProps.name],
                  onFieldBlur: _this2.handleFieldBlur,
                  validationFeedback: validationFeedback[otherProps.name],
                  disabled: isDisabled
                }, otherProps, inferredProps));
              })
            );
          })
        );
      }
    }]);
    return SpWolfForm;
  }(React.Component);

  SpWolfForm.propTypes = {
    onCanSubmitFormChange: PropTypes.func,
    debugOnChange: PropTypes.bool,
    sectionProps: PropTypes.object,
    specs: PropTypes.shape({
      sections: PropTypes.arrayOf(PropTypes.shape({
        meta: PropTypes.object,
        elements: PropTypes.arrayOf(PropTypes.shape({
          name: PropTypes.string.isRequired,
          fieldType: PropTypes.string.isRequired,
          required: PropTypes.bool,
          isPresentationalElement: PropTypes.bool,
          dependsOn: PropTypes.arrayOf(PropTypes.string),
          existsIf: PropTypes.arrayOf(PropTypes.string),
          disabledIf: PropTypes.string,
          onChangeReset: PropTypes.string,
          validationFeedbackRules: PropTypes.arrayOf(PropTypes.shape({
            type: PropTypes.string.isRequired,
            condition: PropTypes.func.isRequired,
            label: PropTypes.string,
            checkOnChange: PropTypes.bool
          }))
        })).isRequired
      })).isRequired,
      conditionalFields: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string.isRequired,
        fn: PropTypes.func.isRequired,
        dependsOn: PropTypes.arrayOf(PropTypes.string)
      }))
    }).isRequired
  };

  SpWolfForm.defaultProps = {
    onCanSubmitFormChange: undefined,
    debugOnChange: false
  };

  SpWolfForm.contextTypes = {
    defineStateGetter: PropTypes.func
  };

  return SpWolfForm;
};
var connect = function connect(Component) {
  var Connected = function (_React$Component2) {
    inherits(Connected, _React$Component2);

    function Connected() {
      var _ref5;

      var _temp, _this3, _ret;

      classCallCheck(this, Connected);

      for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }

      return _ret = (_temp = (_this3 = possibleConstructorReturn(this, (_ref5 = Connected.__proto__ || Object.getPrototypeOf(Connected)).call.apply(_ref5, [this].concat(args))), _this3), _this3.state = { getFormState: undefined }, _this3.defineStateGetter = function (cb) {
        _this3.setState({ getFormState: cb });
      }, _temp), possibleConstructorReturn(_this3, _ret);
    }

    createClass(Connected, [{
      key: 'getChildContext',
      value: function getChildContext() {
        return {
          defineStateGetter: this.defineStateGetter
        };
      }
    }, {
      key: 'render',
      value: function render() {
        return React.createElement(Component, _extends({
          getFormState: this.state.getFormState
        }, this.props || {}));
      }
    }]);
    return Connected;
  }(React.Component);

  Connected.childContextTypes = {
    defineStateGetter: PropTypes.func
  };
  return Connected;
};

export { FormHOC, connect, getHash, calculateHashOfValue as calculateHash, getDiff };
//# sourceMappingURL=index.es.js.map



//////////////////
// WEBPACK FOOTER
// ../dist/index.es.js
// module id = 114
// module chunks = 0